import { OidcClient } from '@axa-fr/react-oidc';
import { MobileAppBanner, parseStringToBoolean, useMessageChannelContext } from '@healthhub/react-ui';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadScript } from '@shared/components';
import { getCAChatbotUrl, getPublicCAChatbotUrl, sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { Category, CategoryType } from '@shared/constants/app-services';
import { CA_ENABLED } from '@shared/constants/systemSettingsKey';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import { useGetMobileAppBannerQuery } from '@shared/services/sitecore';
import {
  useGetAppServicesQuery,
  useGetPublicAppServicesQuery,
  useGetSystemSettingsQuery,
} from '@shared/services/systemSettings';
import { useAppDispatch } from '@shared/stores/rootTypes';
import { userAction } from '@shared/stores/user/userSlice';
import type { ChatbotElement } from '@shared/types/chatbot';

import { useOnLoadScriptCallback } from '@features/Chatbot/shared/hooks/useChatbotCallBacks';
import { useMobileStoreSubscription } from '@features/Chatbot/shared/hooks/useMobileStoreSubscription';
import { LookForEServices } from '@features/Home/shared/components';
import { formatEServices } from '@features/Home/shared/utils';

import Banner from './Banner';
import FamilyHealth from './FamilyHealth';
import LatestTrendingArticles from './LatestTrendingArticles';
import PostLoginDashboard from './PostLoginDashboard';
import PreLoginDashboard from './PreLoginDashboard';

const HomePageContent = () => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { sendMessageToNative } = useMessageChannelContext();
  const oidc = OidcClient.get();
  const { isAuthenticated } = useIsAuthenticated();
  const { data: mobileAppBanner, isLoading: isMobileAppBannerLoading } = useGetMobileAppBannerQuery(undefined, {
    skip: !isAuthenticated,
  });

  const ref = useRef<ChatbotElement>(null);
  const { consumeBridgeMessage, subscribeMessages } = useMobileStoreSubscription();

  const getAppServicesBody = {
    Category: Category.PH,
    CategoryType: CategoryType.SERVICE_CATEGORY,
    Language: i18n.language,
  };

  const { data: systemSettings } = useGetSystemSettingsQuery({
    Categories: [CA_ENABLED],
  });

  const isCaEnabled = parseStringToBoolean(systemSettings?.find((s) => s.Category === CA_ENABLED)?.Value);

  const { data: appServicesData, isFetching: isFetchingAppServices } = useGetAppServicesQuery(getAppServicesBody, {
    skip: !isAuthenticated,
  });
  const { data: publicAppServicesData, isFetching: isFetchingPublicAppServices } = useGetPublicAppServicesQuery(
    getAppServicesBody,
    {
      skip: isAuthenticated,
    }
  );

  const data = appServicesData ?? publicAppServicesData;
  const isFetching = isFetchingAppServices ?? isFetchingPublicAppServices;

  useEffect(() => {
    dispatch(userAction.resetSelectedProfile());
  }, [dispatch]);

  const eservicesFormatted = useMemo(() => formatEServices(data?.ServiceCategories ?? []), [data?.ServiceCategories]);

  const onLoadScript = useOnLoadScriptCallback({
    chatbotRef: ref,
    consumeBridgeMessage,
    isAuthenticated,
    logoutRedirectUri: sitecoreHomeBaseUrl(),
    oidcClient: oidc,
    sendMessageToNative,
    subscribeMessages,
  });

  return (
    <div className="flex flex-col">
      {isAuthenticated ? <PostLoginDashboard /> : <PreLoginDashboard />}
      <Banner />
      <LookForEServices eservices={eservicesFormatted} isFetching={isFetching} />
      <FamilyHealth />
      <LatestTrendingArticles />
      {!!isAuthenticated && (
        <div className="mb-10">
          <MobileAppBanner data={mobileAppBanner} isLoading={isMobileAppBannerLoading} />
        </div>
      )}
      {!!isCaEnabled && (
        <>
          <LoadScript
            async={false}
            defer
            onload={onLoadScript}
            src={isAuthenticated ? getCAChatbotUrl() : getPublicCAChatbotUrl()}
            type="module"
          />

          <ai-chatbot open={false} ref={ref} />
        </>
      )}
    </div>
  );
};

export default HomePageContent;
